import { useId } from 'react';
import { cn } from '../../constants/design-tokens';

/** The Orio360 tagline (docs/rebrand-plan.md §2). */
export const ORIO360_TAGLINE = 'Run everything from one place.';

export type LogoVariant = 'mark' | 'lockup';
export type LogoTone = 'color' | 'white' | 'mono';

export interface LogoProps {
  /** `mark` is the ring alone; `lockup` adds the word "Orio360". Default `lockup`. */
  variant?: LogoVariant;
  /** `color` (full colour, light + dark), `white`, or `mono` (`currentColor`). Default `color`. */
  tone?: LogoTone;
  /** Mark height in px; the word and tagline scale with it. Default 32. */
  size?: number;
  /** Lockup only: render ORIO360_TAGLINE under the name. */
  showTagline?: boolean;
  className?: string;
  /** Accessible name of a standalone mark. Default `'Orio360'`. */
  title?: string;
}

// Proportions of assets/brand/orio360-lockup.svg: mark 64 high, word font-size 34, word starts at x=76.
const WORD_SCALE = 34 / 64;
const GAP_SCALE = 12 / 64;
const TAGLINE_SCALE = 0.28; // floored at 12px so the tagline stays legible on a small mark

function Mark({
  tone,
  size,
  title,
  decorative,
  className,
}: {
  tone: LogoTone;
  size: number;
  title: string;
  decorative: boolean;
  className?: string;
}) {
  // useId() returns ":r0:"-style ids; drop the colons so the url(#...) reference stays a plain name.
  const gradientId = `orio360-ring-${useId().replace(/:/g, '')}`;
  const a11y = decorative
    ? { 'aria-hidden': true, focusable: false }
    : { role: 'img', 'aria-label': title };

  // Geometry copied from assets/brand/orio360-mark.svg, orio360-mark-mono.svg and orio360-mark-white.svg.
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 64 64"
      width={size}
      height={size}
      className={cn('shrink-0', className)}
      {...a11y}
    >
      {tone === 'color' && (
        <defs>
          <linearGradient
            id={gradientId}
            x1="10"
            y1="10"
            x2="54"
            y2="54"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0" stopColor="#3B82F6" className="dark:[stop-color:#60A5FA]" />
            <stop offset="1" stopColor="#1E40AF" className="dark:[stop-color:#6366F1]" />
          </linearGradient>
        </defs>
      )}
      <path
        d="M54 32A22 22 0 1 1 32 10"
        fill="none"
        stroke={
          tone === 'color' ? `url(#${gradientId})` : tone === 'white' ? '#FFFFFF' : 'currentColor'
        }
        strokeWidth="9"
        strokeLinecap="round"
      />
      <circle
        cx="47.56"
        cy="16.44"
        r="5.5"
        fill={tone === 'color' ? '#14B8A6' : tone === 'white' ? '#FFFFFF' : 'currentColor'}
        className={tone === 'color' ? 'dark:fill-teal-400' : undefined}
      />
    </svg>
  );
}

export function Logo({
  variant = 'lockup',
  tone = 'color',
  size = 32,
  showTagline = false,
  className,
  title = 'Orio360',
}: LogoProps) {
  if (variant === 'mark') {
    return <Mark tone={tone} size={size} title={title} decorative={false} className={className} />;
  }

  return (
    <span className={cn('inline-flex items-center', className)} style={{ gap: size * GAP_SCALE }}>
      <Mark tone={tone} size={size} title={title} decorative />
      <span className="flex flex-col">
        <span
          className={cn(
            'font-extrabold leading-none tracking-tight',
            tone === 'color' && 'text-neutral-900 dark:text-white',
            tone === 'white' && 'text-white',
          )}
          style={{ fontSize: size * WORD_SCALE }}
        >
          Orio
          <span
            className={cn('font-bold', tone === 'color' && 'text-brand-600 dark:text-brand-400')}
          >
            360
          </span>
        </span>
        {showTagline && (
          <span
            className={cn(
              'mt-1 font-medium leading-tight',
              tone === 'color' && 'text-neutral-500 dark:text-neutral-400',
              tone === 'white' && 'text-white/80',
            )}
            style={{ fontSize: Math.max(12, size * TAGLINE_SCALE) }}
          >
            {ORIO360_TAGLINE}
          </span>
        )}
      </span>
    </span>
  );
}
